/**
 * The confirmation step for anything destructive, rendered in the page.
 *
 * Not `window.confirm`. A native dialog blocks the event loop, cannot show the
 * account it is about to act on in the app's own type and colour, is styled by
 * the browser rather than by us, and is unassertable in jsdom — so the one
 * guard standing between a click and an irreversible request would be the one
 * thing no test could cover.
 *
 * `role="alertdialog"` gives assistive tech the same "you must answer this"
 * semantics a native dialog has, without a native dialog: the question is the
 * accessible name, and the confirm button says what will happen rather than
 * "OK".
 *
 * Two things a native dialog gives for free and this has to do by hand. Focus
 * moves into the wrapper on mount, because `alertdialog` is only announced when
 * it RECEIVES focus — the prompt opens in a new `<tr>` below the row while
 * focus stays on the trigger, so without this a screen-reader user clicks
 * "Deactivate", hears nothing, and has to tab past the remaining action buttons
 * to discover the question. And Escape cancels, because a destructive prompt
 * with no keyboard way out is one you have to hunt for the Cancel button to
 * escape.
 *
 * `confirmPhrase` adds a second gate for the actions that cannot be undone at
 * all. A prompt whose only control is a button is defeated by muscle memory:
 * the click that opens it and the click that carries it out are the same
 * gesture twice, on a row that looks like every other row in the table. Typing
 * the account's own email is the one confirmation that cannot be produced by
 * habit, because it can only be produced by reading the row you are actually
 * on. Matched EXACTLY -- no trimming, no case folding: "close enough" on an
 * irreversible action means the admin was working from memory, which is the
 * failure this is here to catch.
 */
import { useEffect, useId, useRef, useState } from 'react'
import type { KeyboardEvent } from 'react'

import { INPUT } from '../auth/LoginPage'
import { useI18n } from '../i18n'

export interface ConfirmActionProps {
  /** Names the account and the consequence, not just the verb. */
  question: string
  /** Extra consequences worth spelling out before the click. */
  detail?: string
  /** The affirmative button's label, e.g. "Yes, deactivate". */
  confirmLabel: string
  /**
   * Receives the text the admin typed, when `confirmPhrase` is set (the empty
   * string otherwise). Handed over rather than left here because a caller that
   * has to prove which account was confirmed -- to a server that re-checks it --
   * must send the value a human produced by reading the row, not one it
   * re-derived from the same props that rendered the row.
   */
  onConfirm: (typed: string) => void
  onCancel: () => void
  /** The request is in flight: neither button may fire again. */
  pending?: boolean
  /**
   * When set, the affirmative stays disabled until this exact string is typed.
   * For an irreversible action, pass the thing being destroyed -- an email, not
   * the word "DELETE", so the confirmation names the specific account.
   */
  confirmPhrase?: string
}

export function ConfirmAction({
  question,
  detail,
  confirmLabel,
  onConfirm,
  onCancel,
  pending = false,
  confirmPhrase,
}: ConfirmActionProps) {
  const { t } = useI18n()
  const wrapper = useRef<HTMLDivElement>(null)
  const fieldId = useId()
  const [typed, setTyped] = useState('')
  const unmatched = confirmPhrase !== undefined && typed !== confirmPhrase

  // The wrapper, not the confirm button: landing focus on a destructive
  // affirmative is how a stray Enter deactivates an account.
  useEffect(() => {
    wrapper.current?.focus()
  }, [])

  /**
   * Cancel empties the field on the way out.
   *
   * The parent usually unmounts this, which would clear it anyway -- but only
   * usually. A parent that keeps the prompt mounted, or reopens it for the same
   * row, would otherwise bring back a destructive prompt already armed: the
   * admin declined once, and the next single click would do the thing they
   * declined. Owning the reset here means no caller can get that wrong.
   */
  function cancel() {
    setTyped('')
    onCancel()
  }

  function onKeyDown(event: KeyboardEvent<HTMLDivElement>) {
    // Not while pending: the request is already gone, so "cancelled" would be
    // a lie the UI cannot make true.
    if (event.key === 'Escape' && !pending) {
      event.stopPropagation()
      cancel()
    }
  }

  // `t()` with no params leaves `{{phrase}}` in place, which is what splits.
  const typePrompt = t('account.confirm.typePhrase').split('{{phrase}}')

  return (
    <div
      ref={wrapper}
      role="alertdialog"
      aria-label={question}
      tabIndex={-1}
      onKeyDown={onKeyDown}
      className="space-y-2 rounded-md border border-down/50 bg-down/10 p-3
                 outline-none focus-visible:ring-2 focus-visible:ring-down/60"
    >
      <p className="text-sm font-medium text-fg">{question}</p>
      {detail && <p className="text-xs text-muted">{detail}</p>}

      {confirmPhrase !== undefined && (
        <div className="space-y-1">
          <label htmlFor={fieldId} className="block text-xs text-muted">
            {/* The phrase is in the label, not in a placeholder: a placeholder
                disappears the moment you start typing, which is exactly when
                you need to see what you are matching. One key holds the whole
                sentence -- languages put the phrase in different places -- and
                it is split on its own placeholder so the phrase itself can
                still be rendered in mono. */}
            {typePrompt[0]}
            <span className="font-mono text-fg">{confirmPhrase}</span>
            {typePrompt[1]}
          </label>
          <input
            id={fieldId}
            type="text"
            value={typed}
            onChange={(e) => setTyped(e.target.value)}
            disabled={pending}
            autoComplete="off"
            autoCorrect="off"
            autoCapitalize="none"
            spellCheck={false}
            className={`${INPUT} max-w-sm font-mono`}
          />
        </div>
      )}

      <div className="flex items-center gap-2">
        <button
          type="button"
          onClick={() => onConfirm(typed)}
          disabled={pending || unmatched}
          aria-busy={pending}
          className="rounded-md bg-down px-2.5 py-1 text-xs font-medium text-white disabled:opacity-60"
        >
          {confirmLabel}
        </button>
        <button
          type="button"
          onClick={cancel}
          disabled={pending}
          className="rounded-md border border-border px-2.5 py-1 text-xs text-muted hover:text-fg disabled:opacity-60"
        >
          {t('common.cancel')}
        </button>
      </div>
    </div>
  )
}
